import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import {
  LINE_STYLE_PREVIEW_COMPACT_WIDTH,
  LINE_STYLE_PREVIEW_DEFAULT_STROKE,
  LINE_STYLE_PREVIEW_HEIGHT,
  LINE_STYLE_PREVIEW_MARKER_RADIUS,
  LINE_STYLE_PREVIEW_MAX_STROKE,
  LINE_STYLE_PREVIEW_WIDTH,
} from './line-style-select.constants';
import { LineStyleSelectOption } from './line-style-select.types';
import { markerShapePath } from './line-style-select.utils';

@Component({
  selector: 'firebird-web-line-style-select',
  templateUrl: './line-style-select.component.html',
  styleUrls: ['./line-style-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LineStyleSelectComponent<T extends string = string> {
  @Input() public options: ReadonlyArray<LineStyleSelectOption<T>> = [];
  @Input() public value?: T;
  @Input() public compact = false;
  @Input() public disabled = false;
  @Input() public tooltip = '';

  @Output() public valueChange = new EventEmitter<T>();

  public isOpen = false;

  public readonly previewHeight = LINE_STYLE_PREVIEW_HEIGHT;
  public readonly menuPreviewWidth = LINE_STYLE_PREVIEW_WIDTH;

  public get selected(): LineStyleSelectOption<T> | undefined {
    return this.options.find((option) => option.value === this.value);
  }

  public get previewWidth(): number {
    return this.compact
      ? LINE_STYLE_PREVIEW_COMPACT_WIDTH
      : LINE_STYLE_PREVIEW_WIDTH;
  }

  public hasPreview(option: LineStyleSelectOption<T> | undefined): boolean {
    return !!option?.svgDasharray || !!option?.marker;
  }

  public markerPath(option: LineStyleSelectOption<T>, width: number): string {
    return option.marker
      ? markerShapePath(
          option.marker,
          width / 2,
          this.previewHeight / 2,
          LINE_STYLE_PREVIEW_MARKER_RADIUS
        )
      : '';
  }

  public strokeWidth(option: LineStyleSelectOption<T>): number {
    return Math.min(
      option.lineWidth ?? LINE_STYLE_PREVIEW_DEFAULT_STROKE,
      LINE_STYLE_PREVIEW_MAX_STROKE
    );
  }

  public dasharray(option: LineStyleSelectOption<T>): string | null {
    return option.svgDasharray === 'none' ? null : option.svgDasharray ?? null;
  }

  public select(option: LineStyleSelectOption<T>): void {
    if (option.value !== this.value) {
      this.valueChange.emit(option.value);
    }
  }
}
